/**
 * Statement 01 — Warping Production & Stock Reconciliation (READ-ONLY).
 *
 * Management reconciliation statement over existing authoritative MES data.
 * It posts nothing and mutates nothing — every value is served by
 * GET /api/warping-reconciliation/{cwo_id}. Sections A–E mirror the
 * Owner-confirmed layout; Section D shows Transferred Out (V2 transfer) AND
 * Released to Weaving (consumed in place) as separate mechanisms.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ChartBarIcon, ArrowsClockwiseIcon, CaretRightIcon, CaretDownIcon, PrinterIcon } from "@phosphor-icons/react";

const kg = (n) => (n === null || n === undefined) ? "—" : `${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`;

const STATUS_CLS = (s) => {
  const u = (s || "").toUpperCase();
  if (u === "RECONCILED" || u === "COMPLETE") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
  if (u === "SHORTAGE") return "bg-red-500/15 text-red-300 border-red-500/40";
  if (u === "EXCESS") return "bg-sky-500/15 text-sky-300 border-sky-500/40";
  if (u.includes("PENDING")) return "bg-amber-500/15 text-amber-300 border-amber-500/40";
  if (u.includes("LEGACY")) return "bg-purple-500/15 text-purple-300 border-purple-500/40";
  if (u === "VARIANCE") return "bg-orange-500/15 text-orange-300 border-orange-500/40";
  return "bg-zinc-500/15 text-zinc-300 border-zinc-500/40";
};

const Code = ({ children }) => <span className="font-mono text-[11px] text-white break-all whitespace-normal leading-snug">{children}</span>;
const Num = ({ children, cls = "" }) => <span className={`font-mono tabular-nums ${cls}`}>{children}</span>;
const Badge = ({ s, label }) => <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border whitespace-nowrap ${STATUS_CLS(s)}`}>{label || s}</span>;

// Display-only compact labels for Section E badges. Underlying status values
// (used for colour + any downstream logic) are left unchanged.
const shortStatus = (s) => {
  const u = (s || "").toUpperCase();
  if (u.includes("PENDING")) return "Pending Phy Ver";
  if (u.includes("LEGACY")) return "Legacy Release";
  return s;
};

export default function WarpingReconciliation() {
  const [cwos, setCwos] = useState([]);
  const [cwoId, setCwoId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/warping-reconciliation/cwos");
        setCwos(data || []);
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load Warping CWOs"); }
    })();
  }, []);

  const loadStatement = async (id) => {
    if (!id) { setData(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/warping-reconciliation/${id}`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to load statement"); setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadStatement(cwoId); }, [cwoId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const H = data?.header;
  const cwoLabel = useMemo(() => (c) => `${c.cwo_no} · ${c.parent_wo_no} · ${c.source_order_no || "—"} (${c.status})`, []);

  return (
    <div className="p-6 space-y-5" data-testid="warping-reconciliation-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><ChartBarIcon size={11} weight="bold" />Reports · Reconciliation</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Warping Production &amp; Stock Reconciliation</h1>
          <div className="text-xs text-zinc-500 mt-1">Read-only management statement over existing posted MES data · no stock, production, MI or variance is written from this screen.</div>
        </div>
        <div className="flex items-center gap-2">
          {data && <button onClick={() => window.print()} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2" data-testid="warp-recon-print"><PrinterIcon size={14} weight="bold" />Print</button>}
          <button onClick={() => loadStatement(cwoId)} disabled={!cwoId || loading} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2 disabled:opacity-40" data-testid="warp-recon-refresh">
            <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
          </button>
        </div>
      </div>

      {/* Selection */}
      <div className="tactical-card p-5">
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Parent WO · Warping CWO *</label>
        <select data-testid="warp-recon-cwo-select" value={cwoId} onChange={e => setCwoId(e.target.value)}
                className="h-10 px-3 w-full md:w-[640px] bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]">
          <option value="">— Select a Warping CWO —</option>
          {cwos.map(c => <option key={c.id} value={c.id}>{cwoLabel(c)}</option>)}
        </select>
      </div>

      {loading && <div className="tactical-card p-8 text-center text-zinc-500" data-testid="warp-recon-loading">Loading reconciliation…</div>}

      {data && H && (
        <>
          {/* HEADER */}
          <div className="tactical-card p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px] font-mono" data-testid="warp-recon-header">
            {[["Parent WO", H.parent_wo_no], ["Warping CWO", H.cwo_no], ["SO / ESO", H.so_eso_no], ["Product Code", H.product_code || "—"],
              ["Plan No", H.plan_no || "—"], ["Department", H.department], ["CWO Status", H.cwo_status], ["Reconciliation As-Of", (H.as_of || "").replace("T", " ").slice(0, 19)]].map(([k, v]) => (
              <div key={k}><div className="text-zinc-500 mb-0.5 uppercase tracking-widest text-[9px]">{k}</div><div className="text-white break-all">{v || "—"}</div></div>
            ))}
            {H.product_description && <div className="col-span-2"><div className="text-zinc-500 mb-0.5 uppercase tracking-widest text-[9px]">Description</div><div className="text-white break-all">{H.product_description}</div></div>}
          </div>

          {/* SECTION A */}
          <Section title="A · CWO Requirement Status" testId="section-a">
            <Table head={["RM / SFW Item", "CWO Required", "CWO Produced", "CWO Balance", "Status"]}
                   rows={data.section_a.rows.map((r, i) => [
                     <div key="c"><Code>{r.rm_code}</Code>{r.sfw_code && <div className="text-[10px] text-zinc-500 font-mono break-all">→ {r.sfw_code}</div>}</div>,
                     <Num>{kg(r.required)}</Num>, <Num cls="text-emerald-300">{kg(r.produced)}</Num>,
                     <Num cls="text-amber-300">{kg(r.balance)}</Num>, <Badge s={r.status} />,
                   ])}
                   total={["TOTAL", kg(data.section_a.totals.required), kg(data.section_a.totals.produced), kg(data.section_a.totals.balance), ""]} />
          </Section>

          {/* SECTION B */}
          <Section title="B · Material Issue Accountability" testId="section-b"
                   note="MI Issued is an accountability value against this CWO — NOT a production-consumption ceiling. Reversed/unposted MIs are excluded.">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                  <tr><th className="text-left px-4 py-2 w-6"></th><th className="text-left px-4 py-2">RM Item</th><th className="text-right px-4 py-2">CWO Required</th><th className="text-right px-4 py-2">MI Issued (this CWO)</th><th className="text-right px-4 py-2">Balance to Issue</th></tr>
                </thead>
                <tbody>
                  {data.section_b.rows.map((r, i) => (
                    <>
                      <tr key={i} className="border-b border-[var(--border)]/60 cursor-pointer hover:bg-[var(--bg-2)]/30" onClick={() => setOpen(o => ({ ...o, [r.rm_code]: !o[r.rm_code] }))} data-testid={`section-b-row-${i}`}>
                        <td className="px-4 py-2 text-zinc-400">{(r.detail || []).length > 0 ? (open[r.rm_code] ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />) : null}</td>
                        <td className="px-4 py-2"><Code>{r.rm_code}</Code></td>
                        <td className="px-4 py-2 text-right"><Num>{kg(r.required)}</Num></td>
                        <td className="px-4 py-2 text-right"><Num cls="text-emerald-300">{kg(r.mi_issued)}</Num></td>
                        <td className="px-4 py-2 text-right"><Num cls="text-amber-300">{kg(r.balance_to_issue)}</Num></td>
                      </tr>
                      {open[r.rm_code] && (r.detail || []).map((d, j) => (
                        <tr key={`${i}-${j}`} className="bg-[var(--bg-0)]/40 border-b border-[var(--border)]/30" data-testid={`section-b-detail-${i}-${j}`}>
                          <td></td>
                          <td className="px-4 py-1.5 text-[11px]"><span className="font-mono text-emerald-300">{d.mi_no}</span></td>
                          <td className="px-4 py-1.5 text-right text-[11px] font-mono text-sky-300">{d.batch}</td>
                          <td className="px-4 py-1.5 text-right text-[11px]"><Num>{kg(d.qty)}</Num></td>
                          <td className="px-4 py-1.5 text-right text-[11px] text-zinc-400">{d.date} · {d.status}</td>
                        </tr>
                      ))}
                    </>
                  ))}
                  <tr className="border-t-2 border-[var(--border)] font-semibold text-white">
                    <td></td><td className="px-4 py-2">TOTAL</td>
                    <td className="px-4 py-2 text-right"><Num>{kg(data.section_b.totals.required)}</Num></td>
                    <td className="px-4 py-2 text-right"><Num>{kg(data.section_b.totals.issued)}</Num></td>
                    <td className="px-4 py-2 text-right"><Num>{kg(data.section_b.totals.balance)}</Num></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          {/* SECTION C */}
          <Section title="C · RM Production & Stock Reconciliation" testId="section-c"
                   note="Sequence: Received → Produced → Wastage → Consumption → Balance → Variance. Consumption = Produced + Wastage (BOM wastage 0%). Department Received is the Warping pool and is NOT equated to CWO MI. Physical variance only with physical/return evidence.">
            <Table head={["RM Item", "Received", "Produced", "Wastage", "Consumption", "Book Balance", "Stock V2", "Physical / Returned", "Variance", "Status"]}
                   rows={data.section_c.rows.map((r) => [
                     <Code>{r.rm_code}</Code>, <Num>{kg(r.received)}</Num>, <Num cls="text-emerald-300">{kg(r.produced)}</Num>,
                     <Num>{kg(r.wastage)}</Num>, <Num>{kg(r.consumption)}</Num>, <Num cls="text-amber-300">{kg(r.book_balance)}</Num>,
                     <Num cls="text-sky-300">{kg(r.v2_balance)}</Num>, <span className="text-zinc-500">{r.physical_returned === null ? "—" : kg(r.physical_returned)}</span>,
                     <span className="text-zinc-500">{r.variance === null ? "—" : kg(r.variance)}</span>, <Badge s={r.variance_status} />,
                   ])}
                   total={["TOTAL", kg(data.section_c.totals.received), kg(data.section_c.totals.produced), kg(data.section_c.totals.wastage), kg(data.section_c.totals.consumption), kg(data.section_c.totals.book_balance), kg(data.section_c.totals.v2_balance), "", "", ""]} />
          </Section>

          {/* SECTION D */}
          <Section title="D · SFW Output & Release Reconciliation" testId="section-d"
                   note="Variance = Produced − Transferred Out − Released to Weaving (Consumed in Place) − Balance Stock. Transferred Out = genuine posted Warping→Weaving V2 transfers only. Released = posted Weaving Conversion-Consume of Warping SFW in place.">
            <Table head={["SFW Item", "Produced", "Transferred Out (V2)", "Released to Weaving (In Place)", "Balance Stock", "Variance", "Status"]}
                   rows={data.section_d.rows.map((r) => [
                     <Code>{r.sfw_code}</Code>, <Num cls="text-emerald-300">{kg(r.produced)}</Num>, <Num>{kg(r.transferred_out)}</Num>,
                     <Num cls="text-purple-300">{kg(r.released_consumed_in_place)}</Num>, <Num cls="text-amber-300">{kg(r.balance_stock)}</Num>,
                     <Num>{kg(r.variance)}</Num>, <Badge s={r.status} />,
                   ])}
                   total={["TOTAL", kg(data.section_d.totals.produced), kg(data.section_d.totals.transferred_out), kg(data.section_d.totals.released), kg(data.section_d.totals.balance), kg(data.section_d.totals.variance), ""]} />
          </Section>

          {/* SECTION E */}
          <Section title="E · Reconciliation Control & Exceptions" testId="section-e"
                   note="Accountability/control summary — every relevant RM/SFW reconciliation control is listed, including zero-variance (RECONCILED) rows. Reporting results only; nothing is auto-corrected.">
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed" data-testid="section-e-table">
                <colgroup>
                  <col style={{ width: "40%" }} />
                  <col style={{ width: "23%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "13%" }} />
                </colgroup>
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                  <tr>
                    <th className="px-4 py-2 text-left">Item</th>
                    <th className="px-4 py-2 text-left">Control Type</th>
                    <th className="px-4 py-2 text-right">Expected</th>
                    <th className="px-4 py-2 text-right">Actual</th>
                    <th className="px-4 py-2 text-right">Variance</th>
                    <th className="px-4 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.section_e.rows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-zinc-500">No exceptions</td></tr>}
                  {data.section_e.rows.map((r, i) => (
                    <tr key={i} className="border-b border-[var(--border)]/50 align-top" data-testid={`section-e-row-${i}`}>
                      <td className="px-4 py-2 text-left"><Code>{r.rm_code}</Code></td>
                      <td className="px-4 py-2 text-left"><span className="text-[11px] text-zinc-300 break-words leading-snug">{r.control}</span></td>
                      <td className="px-4 py-2 text-right"><span className="text-zinc-400">{r.expected === null ? "—" : kg(r.expected)}</span></td>
                      <td className="px-4 py-2 text-right"><span className="text-zinc-400">{r.actual === null ? "—" : kg(r.actual)}</span></td>
                      <td className="px-4 py-2 text-right"><span className="text-zinc-400">{r.variance === null ? "—" : kg(r.variance)}</span></td>
                      <td className="px-4 py-2 text-left"><Badge s={r.status} label={shortStatus(r.status)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}

      {!loading && !data && cwoId === "" && (
        <div className="tactical-card p-10 text-center text-zinc-500" data-testid="warp-recon-empty">Select a Warping CWO above to generate the reconciliation statement.</div>
      )}
    </div>
  );
}

const Section = ({ title, note, children, testId }) => (
  <div className="tactical-card overflow-hidden" data-testid={testId}>
    <div className="px-5 py-3 border-b border-[var(--border)] bg-[var(--bg-2)]/30">
      <div className="text-sm font-semibold text-white uppercase tracking-wide">{title}</div>
      {note && <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed max-w-4xl">{note}</div>}
    </div>
    {children}
  </div>
);

const Table = ({ head, rows, total }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
        <tr>{head.map((h, i) => <th key={i} className={`px-4 py-2 ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={head.length} className="px-4 py-6 text-center text-zinc-500">No rows</td></tr>}
        {rows.map((cells, ri) => (
          <tr key={ri} className="border-b border-[var(--border)]/50 align-top">
            {cells.map((c, ci) => <td key={ci} className={`px-4 py-2 ${ci === 0 ? "text-left max-w-[320px]" : "text-right"}`}>{c}</td>)}
          </tr>
        ))}
        {total && (
          <tr className="border-t-2 border-[var(--border)] font-semibold text-white">
            {total.map((c, ci) => <td key={ci} className={`px-4 py-2 ${ci === 0 ? "text-left" : "text-right"}`}>{ci === 0 ? c : <Num>{c}</Num>}</td>)}
          </tr>
        )}
      </tbody>
    </table>
  </div>
);
